import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import { fetchAccountHeads } from "../../redux/slices/accountHeadSlice";
import { fetchBanks } from "../../redux/slices/bankSlice";
import { fetchCashAccounts } from "../../redux/slices/cashSlice";
import { createJournalEntryThunk, updateJournalEntryThunk } from "../../redux/slices/financeSlice";
import { getApiErrorMessage } from "../../utils/apiError";
import { toId } from "../../utils/ids";

const today = () => new Date().toISOString().split("T")[0];

const EMPTY_LINE = { accountKey: "", description: "", debitAmount: 0, creditAmount: 0 };

const EMPTY = {
  journalDate: today(),
  reference: "",
  narration: "",
  lines: [{ ...EMPTY_LINE }, { ...EMPTY_LINE }],
};

const fmt = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });

const isActive = (r) => (r.status || "Active") === "Active";

// A journal line can post to three different masters (AccountHead / Bank / Cash), so a line
// holds a prefixed composite key ("H:<id>", "B:<id>", "C:<id>") instead of a bare id — the
// prefix is what says which master the id belongs to. refOf splits it back into the
// {refType, refId} pair financeService builds the API payload from.
const TYPE_OF = { H: "AccountHead", B: "Bank", C: "Cash" };
const keyOf = (prefix, id) => (toId(id) ? `${prefix}:${id}` : "");
const refOf = (accountKey) => {
  const [prefix, id] = String(accountKey || "").split(":");
  return { refType: TYPE_OF[prefix] || "AccountHead", refId: toId(id) };
};

// Rebuild a line's composite key from a saved record. Entries written before a line could
// name a bank/cash account carry only a head id per side plus a header-level bankID, so a
// side with no head id on such a record is that bank.
const keyFromRecord = (rec, side) => {
  const type = rec[`${side}AccountType`];
  const bankId = rec[`${side}BankID`] ?? rec[`${side}BankId`] ?? null;
  const cashId = rec[`${side}CashID`] ?? rec[`${side}CashId`] ?? null;
  const headId = rec[`${side}AccountHeadID`] ?? rec[`${side}AccountHeadId`] ?? null;
  const headerBank = rec.bankID ?? rec.bankId ?? null;
  if (type === "Bank") return keyOf("B", bankId || headerBank);
  if (type === "Cash") return keyOf("C", cashId);
  if (!headId && (bankId || headerBank)) return keyOf("B", bankId || headerBank);
  if (!headId && cashId) return keyOf("C", cashId);
  return keyOf("H", headId);
};

const JournalModal = ({ show, onClose, editRecord }) => {
  const dispatch = useDispatch();
  const accountHeads = useSelector((s) => s.accountHeads.list);
  const banks = useSelector((s) => s.bank.list);
  const cashAccounts = useSelector((s) => s.cash.list);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  // Cash and bank accounts are postable accounts in their own right — a journal is how money
  // moves between them and an expense/income head — so they belong in the same line picker
  // as the account heads rather than in a separate header field.
  const groupedOptions = useMemo(() => [
    {
      label: "Cash",
      options: cashAccounts.filter(isActive).map((c) => ({
        value: keyOf("C", c.cashID || c.id),
        label: c.cashName || "Cash",
      })),
    },
    {
      label: "Bank Accounts",
      options: banks.filter(isActive).map((b) => ({
        value: keyOf("B", b.bankID || b.id),
        label: b.bankName,
      })),
    },
    {
      label: "Account Heads",
      options: accountHeads.filter(isActive).map((h) => ({
        value: keyOf("H", h.accountID || h.id),
        label: h.accountName,
      })),
    },
  ].filter((g) => g.options.length), [accountHeads, banks, cashAccounts]);

  const flatOptions = useMemo(() => groupedOptions.flatMap((g) => g.options), [groupedOptions]);

  useEffect(() => {
    dispatch(fetchAccountHeads());
    dispatch(fetchBanks());
    dispatch(fetchCashAccounts());
  }, [dispatch]);

  useEffect(() => {
    if (editRecord) {
      // The API stores/returns a flat single debit line + single credit line
      // (debitAccountHeadID/debitAmount/creditAccountHeadID/creditAmount), never a
      // `lines[]` array — reconstruct the 2-row editable view from those flat fields.
      const reconstructedLines = [
        { ...EMPTY_LINE, accountKey: keyFromRecord(editRecord, "debit"), debitAmount: editRecord.debitAmount ?? 0, creditAmount: 0 },
        { ...EMPTY_LINE, accountKey: keyFromRecord(editRecord, "credit"), debitAmount: 0, creditAmount: editRecord.creditAmount ?? 0 },
      ];
      setForm({
        journalDate: (editRecord.journalDate || today()).split("T")[0],
        reference: editRecord.journalNo || editRecord.reference || "",
        narration: editRecord.remarks || editRecord.narration || "",
        lines: editRecord.lines?.length
          ? editRecord.lines.map((l) => ({
              ...EMPTY_LINE,
              ...l,
              accountKey: l.accountKey || keyOf("H", l.accountHeadId ?? l.accountHeadID),
            }))
          : reconstructedLines,
      });
    } else {
      setForm({ ...EMPTY, journalDate: today(), lines: [{ ...EMPTY_LINE }, { ...EMPTY_LINE }] });
    }
  }, [editRecord, show]);

  const setHeader = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const updateLine = (idx, field, value) => {
    setForm((f) => {
      const lines = [...f.lines];
      lines[idx] = { ...lines[idx], [field]: value };
      return { ...f, lines };
    });
  };

  const selectAccount = (idx, opt) => updateLine(idx, "accountKey", opt?.value || "");

  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { ...EMPTY_LINE }] }));
  const removeLine = (idx) => setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

  const totalDebit = form.lines.reduce((s, l) => s + Number(l.debitAmount || 0), 0);
  const totalCredit = form.lines.reduce((s, l) => s + Number(l.creditAmount || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.journalDate) {
      Swal.fire("Required", "Journal date is required.", "warning");
      return;
    }
    if (!isBalanced) {
      Swal.fire("Unbalanced", `Debit (₹${fmt(totalDebit)}) and Credit (₹${fmt(totalCredit)}) totals must match.`, "warning");
      return;
    }
    const validLines = form.lines
      .filter((l) => l.accountKey && (Number(l.debitAmount) > 0 || Number(l.creditAmount) > 0))
      .map((l) => {
        const { refType, refId } = refOf(l.accountKey);
        return { ...l, refType, refId, accountHeadId: refType === "AccountHead" ? refId : null };
      });
    if (validLines.length < 2) {
      Swal.fire("Required", "At least 2 journal lines are required.", "warning");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, lines: validLines };
      // Raw API records key off journalID, never a bare `id` — checking editRecord?.id
      // was always false, so "Save Changes" silently created a new entry instead of
      // updating the one being edited (this is why journal entries "couldn't be edited").
      const recordId = editRecord?.journalID ?? editRecord?.id;
      if (recordId) {
        await dispatch(updateJournalEntryThunk({ id: recordId, data: payload })).unwrap();
      } else {
        await dispatch(createJournalEntryThunk(payload)).unwrap();
      }
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Journal entry saved", showConfirmButton: false, timer: 2000 });
      onClose();
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to save journal entry."), "error");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            <div className="modal-header border-0 custom-modal-header">
              <div className="page-title">
                <h4>{editRecord ? "Edit Journal Entry" : "New Journal Entry"}</h4>
              </div>
              <button type="button" className="close" onClick={onClose}><span>×</span></button>
            </div>
            <div className="modal-body custom-modal-body">
              <form onSubmit={handleSubmit}>
                <div className="row mb-3">
                  <div className="col-lg-3">
                    <label className="form-label">Date <span className="text-danger">*</span></label>
                    <input type="date" className="form-control" value={form.journalDate} onChange={setHeader("journalDate")} />
                  </div>
                  <div className="col-lg-3">
                    <label className="form-label">Reference No.</label>
                    <input className="form-control" value={form.reference} onChange={setHeader("reference")} placeholder="JV-001" />
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label">Narration</label>
                    <input className="form-control" value={form.narration} onChange={setHeader("narration")} placeholder="Brief description of this entry" />
                  </div>
                </div>

                <div className="table-responsive mb-2">
                  <table className="table table-sm table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th style={{ minWidth: 200 }}>Account</th>
                        <th>Description</th>
                        <th style={{ width: 130 }} className="text-end">Debit (₹)</th>
                        <th style={{ width: 130 }} className="text-end">Credit (₹)</th>
                        <th style={{ width: 50 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.lines.map((line, idx) => (
                        <tr key={idx}>
                          <td>
                            <Select
                              classNamePrefix="react-select"
                              options={groupedOptions}
                              value={flatOptions.find((o) => o.value === line.accountKey) || null}
                              onChange={(opt) => selectAccount(idx, opt)}
                              placeholder="Select account..."
                              isSearchable
                              menuPosition="fixed"
                              styles={{ menu: (b) => ({ ...b, zIndex: 9999 }) }}
                            />
                          </td>
                          <td>
                            <input className="form-control form-control-sm" value={line.description}
                              onChange={(e) => updateLine(idx, "description", e.target.value)} />
                          </td>
                          <td>
                            <input type="number" className="form-control form-control-sm text-end" min={0} step="0.01"
                              value={line.debitAmount}
                              onChange={(e) => updateLine(idx, "debitAmount", e.target.value)} />
                          </td>
                          <td>
                            <input type="number" className="form-control form-control-sm text-end" min={0} step="0.01"
                              value={line.creditAmount}
                              onChange={(e) => updateLine(idx, "creditAmount", e.target.value)} />
                          </td>
                          <td className="text-center align-middle">
                            {form.lines.length > 2 && (
                              <button type="button" className="btn btn-sm text-danger p-1" onClick={() => removeLine(idx)}>×</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className={isBalanced ? "table-success" : "table-danger"}>
                        <td colSpan={2} className="text-end fw-bold">
                          Total {!isBalanced && <span className="text-danger small ms-2">⚠ Not Balanced</span>}
                        </td>
                        <td className="text-end fw-bold">₹{fmt(totalDebit)}</td>
                        <td className="text-end fw-bold">₹{fmt(totalCredit)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <button type="button" className="btn btn-sm btn-outline-primary mb-3" onClick={addLine}>+ Add Line</button>

                <div className="modal-footer-btn">
                  <button type="button" className="btn btn-cancel me-2" onClick={onClose}>Cancel</button>
                  <button type="submit" className="btn btn-submit" disabled={saving}>
                    {saving ? "Saving..." : editRecord ? "Save Changes" : "Post Entry"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} />
    </>
  );
};

export default JournalModal;
